/**
 * The client sign-in gate's pure logic (NM-20, phase 3).
 *
 * Its own module for the reason `onboarding.ts` is: the decision that turns a
 * `/api/session` response into "which screen do we show" has real branching, and
 * a branch that only ever runs inside the mounted app only ever gets tested
 * through a browser. `sessionGateState` is a plain function over the parsed
 * response, so the state machine is unit-testable without a DOM.
 */

import type { SessionResp, SessionUser } from '../../../api/schemas.js';
import { apiBase } from '../../app/config.js';

/**
 * What the top-level gate renders, derived from `/api/session`.
 *
 *  - `loading`      — the boot fetch has not answered yet (`session === null`).
 *  - `signed-out`   — no identity: show the sign-in gate. No app, no `/api/state`.
 *  - `unregistered` — a verified identity with no account: the registration flow.
 *  - `blocked`      — signed in but the account is suspended or disabled.
 *  - `active`       — signed in, active, registered: the app itself.
 */
export type SessionState =
  | { status: 'loading' }
  | { status: 'signed-out' }
  | { status: 'unregistered'; email: string | null; displayName: string }
  | { status: 'blocked'; user: SessionUser }
  | { status: 'active'; user: SessionUser };

/**
 * A synthetic signed-out session that **preserves the deploy-constant
 * `appleSignIn` flag** (NM-257). The flag rides the `/api/session` response and
 * the gate reads it to decide whether to show the "Sign in with Apple" button; a
 * bare `{ user: null }` (set on sign-out, or a failed boot probe) drops it, so the
 * button vanishes until the next full reload. Carry the last-known value forward.
 */
export function signedOutSession(prev: SessionResp | null): SessionResp {
  return { user: null, appleSignIn: prev?.appleSignIn ?? false };
}

/** Map a `/api/session` response (or `null`, still loading) to the gate screen. */
export function sessionGateState(resp: SessionResp | null): SessionState {
  if (resp === null) return { status: 'loading' };
  if (resp.user === null) {
    // `pendingRegistration` present is what separates unregistered from signed
    // out — a verified identity that simply has no account yet.
    if (resp.pendingRegistration !== undefined) {
      return { status: 'unregistered', ...resp.pendingRegistration };
    }
    return { status: 'signed-out' };
  }
  if (resp.user.status === 'suspended' || resp.user.status === 'disabled') {
    return { status: 'blocked', user: resp.user };
  }
  return { status: 'active', user: resp.user };
}

/**
 * The narrow slice of `window` the popup sign-in needs, so a test can inject a
 * fake and never open a real window.
 */
type PopupWindow = Pick<Window, 'open' | 'addEventListener' | 'removeEventListener' | 'location'>;

/** Whether a `postMessage` payload is the chooser's completion signal. */
function isAuthMessage(value: unknown): boolean {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'newsmonger-auth';
}

/**
 * Open the dev sign-in chooser in a popup and resolve when it reports success
 * (NM-20), ported from the prototype's `local-session.ts`.
 *
 * The sign-in entry (`/signin` — the stable public path, NM-149) sets the session
 * cookie, then `postMessage`s `{ type: 'newsmonger-auth' }` back to `window.opener`
 * and closes (the persona chooser in dev, the Google flow in prod). This resolves
 * on that message — **validating `event.origin`** against our own so a message
 * from any other window is ignored — and rejects if the popup was blocked. The
 * caller re-fetches `/api/session` to learn who signed in; this only reports that
 * the chooser finished.
 */
function signInVia(entryPath: string, browser: PopupWindow, opts: { redirect?: boolean } = {}): Promise<void> {
  const base = apiBase();
  // Apple uses a **top-level redirect** in a split-origin deploy (NM-258), not a
  // popup: its callback is `response_mode=form_post` and depends on a
  // `SameSite=None` state cookie surviving a cross-site POST, which Safari's ITP
  // blocks inside a popup on a repeat sign-in (no fresh gesture to grant storage
  // access). A top-level navigation keeps the API origin first-party, so the state
  // cookie is never third-party. The callback 302s back to the (allowlisted)
  // frontend URL and the SPA re-boots. The Promise never resolves — we navigate away.
  if (opts.redirect === true && base !== '') {
    browser.location.href = `${base}${entryPath}?return=${encodeURIComponent(`${browser.location.origin}/`)}`;
    return new Promise<void>(() => {
      /* navigating away; never resolves */
    });
  }
  // Otherwise a **popup** (NM-256, the Google flow). Split-origin (NM-188) auth
  // lives on the API origin: the popup opens `<apiBase><entryPath>?return=<this
  // origin>/`, the callback's completion page `postMessage`s back **to this
  // (frontend) origin**, and we validate the message came **from the API origin**.
  // (The old top-level redirect for Google existed only because the completion page
  // used to post to its own origin, which a cross-origin opener never receives —
  // fixed in `completionPage`.) Same-origin (local dev / E2E) opens the relative
  // entry and the message comes from — and is validated against — our own origin.
  const target =
    base !== '' ? `${base}${entryPath}?return=${encodeURIComponent(`${browser.location.origin}/`)}` : entryPath;
  // Where the completion message legitimately comes from: the API origin in a
  // split-origin deploy, else our own origin.
  const expectedOrigin = base !== '' ? new URL(base).origin : browser.location.origin;
  return new Promise<void>((resolve, reject) => {
    const popup = browser.open(target, 'newsmonger-auth', 'popup,width=460,height=640');
    if (popup === null) {
      reject(new Error('The sign-in window was blocked. Allow pop-ups for this site and try again.'));
      return;
    }
    const receive = (event: MessageEvent): void => {
      if (event.origin !== expectedOrigin || !isAuthMessage(event.data)) return;
      browser.removeEventListener('message', receive);
      resolve();
    };
    browser.addEventListener('message', receive);
  });
}

/**
 * Sign in with Google (NM-149, NM-256, NM-261). In a split-origin (production)
 * deploy the popup opens `/auth/google/start`; same-origin dev/E2E opens `/signin`,
 * which there is the persona chooser (`mountDevelopmentAuth`). This is the single
 * Google sign-in path — the same whether the gate is reached from the marketing
 * `/signin` entry (which serves this very gate, NM-261) or from a sign-out.
 */
export function signInGoogle(browser: PopupWindow = window): Promise<void> {
  return signInVia(apiBase() !== '' ? '/auth/google/start' : '/signin', browser);
}

/**
 * Sign in with Apple (NM-252) — the Apple OAuth flow at `/auth/apple/start`, via a
 * **top-level redirect** in a split-origin deploy (NM-258), because Apple's
 * form_post + `SameSite=None` state cookie is blocked inside a popup by Safari's
 * ITP on a repeat sign-in. (Apple is only offered in a split-origin production
 * deploy, so the same-origin popup branch is never exercised for it.)
 */
export function signInApple(browser: PopupWindow = window): Promise<void> {
  return signInVia('/auth/apple/start', browser, { redirect: true });
}
